import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { CheckboxGroup } from '@/components/CheckboxGroup'
import { Button } from '@/components/elements/button'
import { FormField, FormItem, FormControl, FormMessage, Form } from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { RadioGroup } from '@/components/RadioGroup'
import { randomizeArray } from '@/utils/helpers'

import DragAndDropQuestion from './QuizQuestionDragAndDrop'

function SingleChoiceQuestion({ question, field }) {
  const options = useMemo(
    () =>
      randomizeArray(
        question.answers.map((option) => ({
          id: option.title,
          label: option.title,
        })),
      ),
    [question],
  )
  return <RadioGroup large options={options} {...field} />
}
SingleChoiceQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
}

function TrueFalseQuestion({ field, question }) {
  const options = useMemo(
    () =>
      randomizeArray(
        question.answers.map((option) => ({
          id: option.title,
          label: option.title,
        })),
      ),
    [question],
  )
  return <RadioGroup large options={options} {...field} />
}

TrueFalseQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
}

function MultipleChoiceQuestion({ question, field }) {
  const options = useMemo(
    () =>
      randomizeArray(
        question.answers.map((option) => ({
          id: option.title,
          label: option.title,
        })),
      ),
    [question],
  )
  return <CheckboxGroup id={field.name} options={options} large {...field} />
}
MultipleChoiceQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
}

function NumericQuestion({ field }) {
  return <Input type="number" {...field} className="w-96 max-w-full text-2xl md:text-2xl" />
}

NumericQuestion.propTypes = {
  field: PropTypes.object.isRequired,
}

function questionInput({ question, field, control }) {
  switch (question.type) {
    case 'Single Choice':
      return <SingleChoiceQuestion question={question} field={field} />
    case 'Multiple Choice':
      return <MultipleChoiceQuestion question={question} field={field} />
    case 'True/False':
      return <TrueFalseQuestion field={field} question={question} />
    case 'Numeric':
      return <NumericQuestion field={field} />
    case 'Drag and Drop':
      return <DragAndDropQuestion question={question} field={field} control={control} />
    default:
      return <p>Unknown question type: {question.type}</p>
  }
}

const defaultValueForQuestion = ({ type, answers }) => {
  switch (type) {
    case 'Single Choice':
      return ''
    case 'Multiple Choice':
      return []
    case 'True/False':
      return ''
    case 'Numeric':
      return ''
    case 'Drag and Drop':
      return new Array(answers.template.filter((word) => !word).length).fill({ value: '' })
    default:
      return ''
  }
}

function QuizQuestion({ question, setAnswer, isLastQuestion }) {
  const form = useForm({
    defaultValues: { question: defaultValueForQuestion(question) },
  })
  const onSubmit = (data) => {
    setAnswer(data.question)
  }
  const rules =
    question.type === 'Drag and Drop'
      ? { validate: (value) => value.every((v) => v.value) }
      : { required: 'This field is required' }
  return (
    <Form {...form}>
      <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem className="text-2xl">
              <FormControl>
                <div className={question.signedImagePath ? 'grid grid-cols-2 gap-4' : ''}>
                  {questionInput({ question, field, control: form.control })}
                  {question.signedImagePath && (
                    <img
                      src={question.signedImagePath}
                      alt="Question related"
                      className="max-h-96 max-w-full rounded border"
                    />
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
          rules={rules}
        />
      </form>
      <div className="py-4">
        <Button
          type="submit"
          disabled={!form.formState.isValid}
          onClick={form.handleSubmit(onSubmit)}
        >
          {isLastQuestion ? 'Submit Quiz' : 'Next Question'}
        </Button>
      </div>
    </Form>
  )
}

export default QuizQuestion

QuizQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  setAnswer: PropTypes.func.isRequired,
  isLastQuestion: PropTypes.bool.isRequired,
}
